<script lang="ts" setup>
import GifIcon from '@/components/common/icons/GifIcon.vue'
import { Platform } from '@/helper/const'
import { emitter } from '@/main'
import { Capacitor } from '@capacitor/core'
import {
  Attachment01Icon,
  Camera01Icon,
  FaceSmileIcon,
  File05Icon,
  Hash02Icon,
  Image01Icon,
  Link01Icon,
  MessageChatSquareIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UIDrawerContent, UISwitch, UITextMdNormal } from '@gohighlevel/ghl-ui'
import { Editor } from '@tiptap/vue-3'
import { NDrawer, NDrawerContent } from 'naive-ui'
import { onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  editor: Editor,
  selectedEmbed: String,
  editorType: String,
  isWideScreen: Boolean,
  autoFocus: Boolean,
  isCommentDisabled: Boolean,
})

// const isPostEditorDrawerOpen = ref(true)
const isDrawerOpen = ref(false)
const showToolbar = ref(false)
const startY = ref(0)
const virtualKeyboardHeight = ref(0)
const { t } = useI18n()
const localIsCommentDisabled = ref(props.isCommentDisabled || false)

const openDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value
}

watch(
  () => props.isCommentDisabled,
  newValue => {
    localIsCommentDisabled.value = newValue || false
  }
)

emitter.on('embed:show-toolbar', () => {
  showToolbar.value = true
})

onBeforeMount(() => {
  if (
    props.editorType === 'PostEditor' ||
    props.editorType === 'CommentEditor'
  ) {
    embedIcons.splice(3, 0, {
      id: 'link',
      title: t('embeds.addLink'),
      component: Link01Icon,
      suggestion: '',
      showOnMobile: true,
      reaction: () => toggleShowModal('link'),
    })
  }
})

function handleGeometryChange(changeEvent) {
  const { height } = changeEvent.target.boundingRect
  virtualKeyboardHeight.value = height
}

onMounted(() => {
  if (props.autoFocus && props.editorType === 'PostEditor') {
    showToolbar.value = true
  }

  if (props.editorType === 'ChatEditor') {
    // find and remove hashtag from embedIcons
    const hashtagIndex = embedIcons.findIndex(icon => icon.id === 'hashtag')
    embedIcons.splice(hashtagIndex, 1)
  }

  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.addEventListener(
      'geometrychange',
      handleGeometryChange
    )
  }
})

onUnmounted(() => {
  isDrawerOpen.value = false
  showToolbar.value = false
})

const emit = defineEmits([
  'add-attachment',
  'update:selectedEmbed',
  'update:isCommentDisabled',
])

function toggleShowModal(type: string) {
  emit('update:selectedEmbed', type)
  // close the drawer when user picks type of attachment
  //isDrawerOpen.value = false
  //isPostEditorDrawerOpen.value = false
  //showToolbar.value = true
}

const embedIcons = [
  {
    id: 'image',
    title: t('embeds.takePhoto'),
    component: Camera01Icon,
    suggestion: t('embeds.imageSuggestion'),
    showOnMobile: true,
    reaction: () => toggleShowModal('image'),
  },
  {
    id: 'video',
    title: t('embeds.uploadPhotoVideo'),
    component: Image01Icon,
    suggestion: t('embeds.videoSuggestion'),
    showOnMobile: true,
    reaction: () => toggleShowModal('video'),
  },
  {
    id: 'attachment',
    title: t('embeds.selectFile'),
    component: File05Icon,
    suggestion: t('embeds.attachmentSuggestion'),
    showOnMobile: true,
    reaction: () => toggleShowModal('attachment'),
  },
  {
    id: 'gif',
    title: t('embeds.addGif'),
    component: GifIcon,
    limitToOne: false,
    suggestion: t('embeds.gifSuggestion'),
    showOnMobile: true,
    reaction: () => toggleShowModal('gif'),
  },
  {
    id: 'emoji',
    title: t('embeds.addEmoji'),
    component: FaceSmileIcon,
    suggestion: t('embeds.emojiSuggestion'),
    showOnMobile: false,
    reaction: () => toggleShowModal('emoji'),
  },
  {
    id: 'hashtag',
    title: t('embeds.addHashtag'),
    component: Hash02Icon,
    suggestion: t('embeds.hashtagSuggestion'),
    showOnMobile: true,
    reaction: () => toggleShowModal('hashtag'),
  },
]

function onTouchStart(event) {
  startY.value = event.touches[0].clientY
  if (
    window.visualViewport &&
    window.visualViewport?.height < window.innerHeight
  ) {
    // Show toolbar
    const toolbar = document.getElementById('interaction-toolbar')
    if (toolbar && window.visualViewport) {
      // Toolbar height = Keyboard height + 75px
      toolbar.style.height = `${
        window.innerHeight - window.visualViewport.height + 75
      }px`
    }
    showToolbar.value = true
  }
}

function onTouchMove(event) {
  const isKeyboardOpen =
    (window.visualViewport &&
      window.visualViewport?.height < window.innerHeight) ||
    virtualKeyboardHeight.value !== 0 ||
    (Capacitor.getPlatform() === Platform.IOS &&
      window.innerHeight < window.screen.height * 0.8)

  if (isKeyboardOpen) {
    showToolbar.value = true
    if (document.activeElement) {
      ;(document.activeElement as any).blur()
    }
    return
  }

  // Only process swipes if keyboard is closed
  const currentY = event.touches[0].clientY
  const deltaY = currentY - startY.value
  if (deltaY < -50) {
    // Swipe up - show full drawer only if keyboard is not open
    showToolbar.value = false
  } else if (deltaY > 50) {
    // Swipe down - always collapse to toolbar view
    showToolbar.value = true
  }
}

function handleMediaUpload(icons) {
  emitter.emit('useraction:media-upload')
  setTimeout(() => {
    icons.reaction()
  }, 100)
}

function handleToggleUpdate(newValue) {
  emit('update:isCommentDisabled', newValue)
}
</script>
<template>
  <div
    v-if="props.editorType === 'PostEditor'"
    class="posteditor-embed-drawer"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
  >
    <NDrawer
      id="interaction-toolbar"
      class="shadow-md"
      :show="true"
      :block-scroll="false"
      :show-mask="false"
      :z-index="2002"
      :mask-closable="false"
      :placement="'bottom'"
      :height="
        virtualKeyboardHeight === 0
          ? showToolbar
            ? '75px'
            : '505px'
          : `${virtualKeyboardHeight + 75}px`
      "
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
    >
      <NDrawerContent>
        <div class="pb-2 pt-1">
          <div class="flex h-2 w-full flex-col items-center justify-center">
            <div
              class="h-1.5 w-10 rounded-md border border-solid border-communities-border bg-communities-border"
            />
          </div>
        </div>
        <div v-if="showToolbar">
          <div
            class="flex w-full flex-row items-start justify-start gap-6 overflow-x-scroll pl-4"
          >
            <div
              v-for="icons in embedIcons.filter(i => i.showOnMobile)"
              id="media-embed-button"
              :key="icons.id"
              class="embed-item flex flex-row hover:cursor-pointer"
              tabindex="0"
              role="button"
              aria-label="Add Media"
              @click="handleMediaUpload(icons)"
              @keyup.enter="handleMediaUpload(icons)"
            >
              <div
                v-if="icons.showOnMobile"
                class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-communities-surface text-communities-font-secondary"
              >
                <component :is="icons.component" class="h-4 w-4" />
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <!-- Turn off commenting toggle -->
          <div
            class="flex items-center gap-4 border-b border-communities-border px-4 py-4"
            @click.stop
            @touchstart.stop
          >
            <div class="flex flex-1 items-center gap-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-md bg-communities-surface text-communities-font-secondary"
              >
                <MessageChatSquareIcon class="h-5 w-5" />
              </div>
              <UITextMdNormal class="text-communities-font-primary">
                {{ t('post.turnOffCommenting') }}
              </UITextMdNormal>
            </div>
            <UISwitch
              id="comments-disabled-switch"
              v-model:value="localIsCommentDisabled"
              :unchecked-value="false"
              :checked-value="true"
              @update:value="handleToggleUpdate"
              @click.stop
              @touchstart.stop
            />
          </div>

          <!-- Embed icons list -->
          <div
            v-for="icons in embedIcons"
            id="media-embed-button"
            :key="icons.id"
            class="embed-item overflow-hidden hover:cursor-pointer"
            tabindex="0"
            role="button"
            aria-label="Add Media"
            @click="handleMediaUpload(icons)"
            @keyup.enter="handleMediaUpload(icons)"
          >
            <div
              v-if="icons.showOnMobile"
              class="inline-flex h-full w-[95%] items-center justify-start gap-4 rounded-lg bg-communities-background py-4 pl-4"
            >
              <div
                class="inline-flex flex-row items-start justify-center gap-4"
              >
                <div class="flex items-center justify-center gap-4">
                  <div class="relative h-12 w-12">
                    <div class="h-12 w-12 rounded-md bg-communities-surface">
                      <div
                        class="flex h-12 w-12 items-center justify-center rounded-full"
                      >
                        <div
                          class="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-communities-surface text-communities-font-secondary"
                        >
                          <component
                            :is="icons.component"
                            class="h-5 w-5 px-2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="inline-flex flex-col items-start justify-start">
                    <UITextMdNormal
                      class="leading-tight text-communities-font-primary"
                    >
                      {{ icons.title }}
                    </UITextMdNormal>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </NDrawerContent>
    </NDrawer>
  </div>
  <div v-else>
    <div
      class="flex text-communities-font-secondary sm:items-center sm:justify-center"
    >
      <div
        class="relative flex inline-flex h-8 w-8 flex-col items-start items-center justify-start justify-center"
        :class="
          editorType == 'PostEditor'
            ? 'mr-4 rounded-full bg-gray-200'
            : 'rounded-full bg-transparent'
        "
        tabindex="0"
        role="button"
        aria-label="Open Embeds Drawer"
        @click="openDrawer"
        @keyup.enter="openDrawer"
      >
        <Attachment01Icon class="h-5 w-5" />
      </div>
      <NDrawer
        id="embeds-drawer"
        v-model:show="isDrawerOpen"
        class="rounded-t-xl bg-communities-background px-3 pb-3 pt-2"
        :placement="'bottom'"
        :z-index="2002"
        :show-mask="false"
        :height="embedIcons.filter(i => i.showOnMobile).length * 82"
      >
        <UIDrawerContent
          id="embeds-drawer-menu"
          v-swipe="{
            direction: 'down',
            callback: () => (isDrawerOpen = !isDrawerOpen),
          }"
          class="p-0"
          :closable="false"
        >
          <div class="flex h-1 w-full flex-col items-center justify-center">
            <div
              class="h-1 w-16 rounded-md border border-solid border-communities-border bg-communities-border"
            />
          </div>

          <!-- Embed icons list -->
          <div
            v-for="icons in embedIcons"
            id="media-embed-button"
            :key="icons.id"
            class="embed-item hover:cursor-pointer"
            tabindex="0"
            role="button"
            aria-label="Add Media"
            @click="
              () => {
                icons.reaction()
                isDrawerOpen = false
              }
            "
            @keyup.enter="
              () => {
                icons.reaction()
                isDrawerOpen = false
              }
            "
          >
            <div
              v-if="icons.showOnMobile"
              class="inline-flex h-full w-[95%] items-center justify-start gap-4 rounded-lg bg-communities-background py-4 pl-4"
            >
              <div
                class="inline-flex flex-col items-start justify-center gap-4"
              >
                <div class="inline-flex items-center justify-start gap-4">
                  <div class="relative h-10 w-10">
                    <div class="h-10 w-10 rounded-md bg-communities-surface">
                      <div
                        class="flex h-10 w-10 items-center justify-center rounded-full"
                      >
                        <div
                          class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-communities-surface text-communities-font-secondary"
                        >
                          <component
                            :is="icons.component"
                            class="h-4 w-4 px-2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="inline-flex flex-col items-start justify-start">
                    <UITextMdNormal
                      class="leading-tight text-communities-font-primary"
                    >
                      {{ icons.title }}
                    </UITextMdNormal>
                    <!-- <UITextXsMedium
                    class="leading-[18px] text-communities-font-secondary"
                  >
                    {{ icons.suggestion }}
                  </UITextXsMedium> -->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </UIDrawerContent>
      </NDrawer>
    </div>
  </div>
</template>
<style scoped>
#media-embed-button {
  box-sizing: revert !important;
}
#interaction-toolbar {
  background: white;
  border-top-left-radius: 16px !important;
  border-top-right-radius: 16px !important;
}
</style>
<style>
.posteditor-embed-drawer,
.n-drawer.n-drawer--bottom-placement {
  box-shadow: 0 6px 16px -9px rgba(0, 0, 0, 0.8),
    0 9px 28px 0 rgba(0, 0, 0, 0.15), 0 12px 48px 16px rgba(0, 0, 0, 0.08) !important;
}
</style>
